import type { LoaderFunctionArgs } from 'react-router-dom'
import type { Device } from '@/api/definition'
import {
  TransferState,
  DefaultFetchDeviceFilters,
  DurationTestStatus,
} from '@/api/definition'
import { fetchDevicePageApi } from '@/api/devicePageApi' // Import the API function directly
import { ID_TOKEN_NAMESPACE, Profile } from '@/auth'

export interface InServiceLoaderData {
  initialDevices: Device[]
  rowCount: number
  error?: string
}

interface TokenPayload {
  privileges?: string[]
  sub?: string
  iat?: number
  exp?: number
}

// Router loaders run before React components mount, so React hooks are not available.
const getUserProfileFromToken = (): Profile | null => {
  try {
    // Use ID token instead of access token for profile data
    const idToken = localStorage.getItem(ID_TOKEN_NAMESPACE)
    if (!idToken) {
      console.log('No ID token found in localStorage')
      return null
    }

    // Validate JWT format
    const tokenParts = idToken.split('.')
    if (tokenParts.length !== 3) {
      console.error(
        'Invalid ID token JWT format - expected 3 parts, got:',
        tokenParts.length,
      )
      return null
    }

    // Get the payload (middle part)
    let payloadBase64 = tokenParts[1]
    // Add padding if needed
    while (payloadBase64.length % 4) {
      payloadBase64 += '='
    }

    // Replace URL-safe characters if present
    payloadBase64 = payloadBase64.replaceAll('-', '+').replaceAll('_', '/')

    const decodedPayload = atob(payloadBase64)
    const payload: TokenPayload = JSON.parse(decodedPayload)

    // Calculate productionOnly from privileges
    const privileges = payload.privileges
    const productionOnly = !(
      Array.isArray(privileges) && privileges.includes('pm.service')
    )

    return {
      productionOnly,
    }
  } catch (error) {
    console.error('Failed to decode ID token:', error)
    return null
  }
}

export const getDeviceFiltersForUser = (productionOnly?: boolean) => {
  return {
    ...DefaultFetchDeviceFilters,
    inService: true,
    durationTestStatus: productionOnly
      ? [DurationTestStatus.DurationTestCompleted]
      : [
          DurationTestStatus.DurationTestNotStarted,
          DurationTestStatus.DurationTestCompleted,
        ],
    transferStatus: [TransferState.DOMESTIC, TransferState.FOREIGN],
  }
}

export async function TakeOutOfServiceLoader(
  _args: LoaderFunctionArgs,
): Promise<InServiceLoaderData> {
  try {
    // Get user profile from stored token
    const profile = getUserProfileFromToken()

    // Use the shared filter function to ensure consistency
    const deviceFilters = getDeviceFiltersForUser(profile?.productionOnly)

    const result = await fetchDevicePageApi(
      deviceFilters,
      {
        pageIndex: 0,
        pageSize: 10,
        sortBy: [],
        globalFilter: '',
        selectedFilters: {},
      },
      false,
    )

    const transformedData = result.data?.length
      ? result.data.map((device) => ({
          ...device,
          id: device.uid,
        }))
      : []

    return {
      initialDevices: transformedData,
      rowCount: result.rowCount ?? 0,
    }
  } catch (error) {
    console.error('Failed to load initial device data for in service:', error)
    return {
      initialDevices: [],
      rowCount: 0,
      error: 'Failed to load initial device data for in service',
    }
  }
}

export async function VerifyDeviceStatusLoader(
  _args: LoaderFunctionArgs,
): Promise<InServiceLoaderData> {
  try {
    const result = await fetchDevicePageApi(
      {
        ...DefaultFetchDeviceFilters,
        inService: true,
        durationTestStatus: [DurationTestStatus.DurationTestNotStarted],
        transferStatus: [TransferState.IN_PROGRESS],
      },
      {
        pageIndex: 0,
        pageSize: 10,
        sortBy: [],
        globalFilter: '',
        selectedFilters: {},
      },
      false,
    )

    const transformedData = result.data?.length
      ? result.data.map((device) => ({
          ...device,
          id: device.uid,
        }))
      : []

    return {
      initialDevices: transformedData,
      rowCount: result.rowCount ?? 0,
    }
  } catch (error) {
    console.error('Failed to load initial device data for in service:', error)
    return {
      initialDevices: [],
      rowCount: 0,
      error: 'Failed to load initial device data for in service',
    }
  }
}

export const fetchDeviceVerifyCount = async (): Promise<number> => {
  try {
    const result = await fetchDevicePageApi(
      {
        ...DefaultFetchDeviceFilters,
        inService: true,
        durationTestStatus: [DurationTestStatus.DurationTestNotStarted],
        transferStatus: [TransferState.IN_PROGRESS],
      },
      {
        pageIndex: 0,
        pageSize: 1,
        sortBy: [],
        globalFilter: '',
        selectedFilters: {},
      },
      false,
    )

    return result.rowCount ?? 0
  } catch (error) {
    console.error('Error fetching device verify count:', error)
    return 0
  }
}
